<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { compare, prettyHtml, formatMarkdown } from './helpers/textDiffHelper';

import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import KnowledgePanel from './components/KnowledgePanel.vue';

const props = defineProps({
  preview: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['exitPreview']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const uiFlags = useMapGetter('wgptBotKnowledgeManager/getUIFlags');
const knowledge = useMapGetter('wgptBotKnowledgeManager/getKnowledge');

const botId = computed(() => Number(route.params.botId));

const previewContent = computed(() => props.preview.content?.content || '');

const hasPendingChanges = computed(() => {
  return knowledge.value !== previewContent.value;
});

const diffHtml = computed(() => {
  const original = knowledge.value || '';
  if (original === previewContent.value) {
    return formatMarkdown(previewContent.value);
  }

  return prettyHtml(compare(original, previewContent.value));
});

const actionsDisabled = computed(() => {
  return (
    !hasPendingChanges.value ||
    uiFlags.value.isFetching ||
    uiFlags.value.isUpdating ||
    uiFlags.value.isSending
  );
});

const handleKeep = async () => {
  await store.dispatch('wgptBotKnowledgeManager/rollback', {
    botId: botId.value,
    version: props.preview.content.version,
  });
  emit('exitPreview');
};

const handleExit = () => {
  emit('exitPreview');
};
</script>

<template>
  <KnowledgePanel
    :diff-html="diffHtml"
    :is-fetching="uiFlags.isFetching"
    :is-available="uiFlags.isAvailable"
  >
    <template #actions>
      <Button
        :label="
          t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_MANAGER.PREVIEW_SECTION.KEEP_BUTTON')
        "
        class="!pe-2"
        sm
        ghost
        slate
        :disabled="actionsDisabled"
        :is-loading="uiFlags.isUpdating"
        @click="handleKeep"
      />
      <Button
        :label="
          t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_MANAGER.PREVIEW_SECTION.EXIT_BUTTON')
        "
        class="!px-2"
        sm
        ghost
        slate
        @click="handleExit"
      />
      <Icon icon="i-lucide-file-diff" class="text-sm !w-8 text-white" />
    </template>
  </KnowledgePanel>
</template>
